/**
 * UTILIDADES MATEMÁTICAS PARA AR
 * Funciones para cálculos 3D→2D, proyecciones y transformaciones
 */

import { HITBOX_CONFIG } from '@config/constants';
import type { 
  AFrameElement, 
  ScreenArea, 
  ClickData, 
  Point2D,
  RelativeCoordinates,
  AbsoluteCoordinates
} from '@types';

// Local type definition for Button3DData
type Button3DData = {
  position: THREE.Vector3;
  rotation: THREE.Quaternion;
  width: number;
  height: number;
};
import * as THREE from 'three';
import type { Vector3, Quaternion, Camera } from 'three';

/**
 * Calcula la proyección 3D→2D de un botón AR
 */
export function calculateButtonScreenArea(
  buttonElement: AFrameElement, 
  camera: Camera, 
  _canvas: HTMLCanvasElement
): ScreenArea | null {
  try {
    // 1. OBTENER DATOS 3D DEL BOTÓN
    const buttonData = extractButton3DData(buttonElement);
    
    // 2. CALCULAR ESCALA BASADA EN DISTANCIA
    const scale = calculateAdaptiveScale(buttonData.position, camera.position);
    
    // 3. GENERAR ESQUINAS CON PADDING (TU CONFIGURACIÓN x10)
    const corners3D = generatePaddedCorners(buttonData.width, buttonData.height);
    
    // 4. APLICAR TRANSFORMACIONES 3D
    const transformedCorners = applyTransformations(
      corners3D, 
      buttonData.rotation, 
      buttonData.position, 
      scale
    );
    
    // 5. PROYECTAR A COORDENADAS 2D DE PANTALLA
    const screenCorners = projectToScreen(transformedCorners, camera);
    
    // 6. CALCULAR BOUNDING BOX
    const boundingBox = calculateBoundingBox(screenCorners);
    
    return {
      ...boundingBox,
      corners: screenCorners,
      rotation: buttonData.rotation
    };
    
  } catch (error) {
    console.error('Error en calculateButtonScreenArea:', error);
    return null;
  }
}

/**
 * Extrae datos 3D del botón A-Frame
 */
function extractButton3DData(buttonElement: AFrameElement): Button3DData {
  // Posición mundial
  const buttonPos = new THREE.Vector3();
  buttonElement.object3D.getWorldPosition(buttonPos);
  
  // Rotación mundial
  const buttonRotation = new THREE.Quaternion();
  buttonElement.object3D.getWorldQuaternion(buttonRotation);
  
  // Dimensiones
  const geometry = buttonElement.getAttribute('geometry');
  const buttonWidth: number = geometry?.width || 2;
  const buttonHeight: number = geometry?.height || 0.8;
  
  return {
    position: buttonPos,
    rotation: buttonRotation,
    width: buttonWidth,
    height: buttonHeight
  };
}

/**
 * Calcula escala adaptativa basada en distancia
 */
function calculateAdaptiveScale(buttonPos: Vector3, cameraPos: Vector3): number {
  const distance = buttonPos.distanceTo(cameraPos);
  const scaleLimits = HITBOX_CONFIG.SCALE_LIMITS;
  if (!scaleLimits) {
    throw new Error('HITBOX_CONFIG.SCALE_LIMITS is undefined');
  }
  const { min, max, baseFactor } = scaleLimits;
  
  return Math.max(min, Math.min(max, baseFactor / distance));
}

/**
 * Genera esquinas 3D con padding configurado
 */
function generatePaddedCorners(buttonWidth: number, buttonHeight: number): Vector3[] {
  const {
    PADDING_MULTIPLIER_WIDTH = 0.1,
    PADDING_MULTIPLIER_HEIGHT = 0.1
  } = HITBOX_CONFIG;
  
  // Aplicar tu configuración de padding x10
  const paddingWidth = buttonWidth * PADDING_MULTIPLIER_WIDTH;
  const paddingHeight = buttonHeight * PADDING_MULTIPLIER_HEIGHT;
  
  const totalWidth = buttonWidth + paddingWidth;
  const totalHeight = buttonHeight + paddingHeight;
  
  return [
    new THREE.Vector3(-totalWidth/2, -totalHeight/2, 0),  // Inferior izquierda
    new THREE.Vector3(totalWidth/2, -totalHeight/2, 0),   // Inferior derecha
    new THREE.Vector3(totalWidth/2, totalHeight/2, 0),    // Superior derecha
    new THREE.Vector3(-totalWidth/2, totalHeight/2, 0)    // Superior izquierda
  ];
}

/**
 * Aplica transformaciones 3D (rotación, escala, posición)
 */
function applyTransformations(
  corners3D: Vector3[], 
  rotation: Quaternion, 
  position: Vector3, 
  scale: number
): Vector3[] {
  return corners3D.map(corner => {
    // Crear copia para no mutar el original
    const transformedCorner = corner.clone();
    
    // 1. Aplicar rotación
    transformedCorner.applyQuaternion(rotation);
    
    // 2. Aplicar escala
    transformedCorner.multiplyScalar(scale * (HITBOX_CONFIG.PROJECTION_SCALE ?? 1));
    
    // 3. Trasladar a posición mundial
    transformedCorner.add(position);
    
    return transformedCorner;
  });
}

/**
 * Proyecta puntos 3D a coordenadas 2D de pantalla
 */
function projectToScreen(corners3D: Vector3[], camera: Camera): Point2D[] {
  return corners3D.map(corner => {
    const screenPos = corner.project(camera);
    return {
      x: (screenPos.x + 1) / 2,      // Convertir [-1,1] → [0,1]
      y: (1 - screenPos.y) / 2       // Invertir Y y convertir [-1,1] → [0,1]
    };
  });
}

/**
 * Calcula bounding box de las esquinas proyectadas
 */
function calculateBoundingBox(screenCorners: Point2D[]): Omit<ScreenArea, 'corners' | 'rotation'> {
  const xs = screenCorners.map(c => c.x);
  const ys = screenCorners.map(c => c.y);
  
  return {
    x1: Math.max(0, Math.min(...xs)),
    y1: Math.max(0, Math.min(...ys)),
    x2: Math.min(1, Math.max(...xs)),
    y2: Math.min(1, Math.max(...ys))
  };
}

/**
 * Convierte coordenadas de click a relativas
 */
export function convertClickToRelative(event: MouseEvent | TouchEvent, sceneElement: HTMLElement): ClickData {
  const rect = sceneElement.getBoundingClientRect();
  
  // Manejar tanto mouse como touch events
  const clientX = 'clientX' in event ? event.clientX : event.touches[0]?.clientX || 0;
  const clientY = 'clientY' in event ? event.clientY : event.touches[0]?.clientY || 0;
  
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  
  return {
    absolute: { x, y },
    relative: {
      x: x / rect.width,
      y: y / rect.height
    },
    sceneRect: rect
  };
}

/**
 * Convierte coordenadas relativas a píxeles absolutos
 */
export function convertRelativeToPixels(
  relativeCoords: RelativeCoordinates, 
  sceneRect: DOMRect
): AbsoluteCoordinates {
  return {
    x: sceneRect.left + relativeCoords.x * sceneRect.width,
    y: sceneRect.top + relativeCoords.y * sceneRect.height
  };
}

/**
 * Calcula la distancia entre dos puntos 2D
 */
export function calculateDistance2D(point1: Point2D, point2: Point2D): number {
  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calcula el ángulo entre dos puntos
 */
export function calculateAngle(point1: Point2D, point2: Point2D): number {
  return Math.atan2(point2.y - point1.y, point2.x - point1.x);
}

/**
 * Rota un punto alrededor de un centro
 */
export function rotatePoint2D(point: Point2D, center: Point2D, angle: number): Point2D {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos
  };
}

/**
 * Interpola linealmente entre dos valores
 */
export function lerp(start: number, end: number, factor: number): number {
  return start + (end - start) * Math.max(0, Math.min(1, factor));
}

/**
 * Clamp un valor entre min y max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Convierte grados a radianes
 */
export function degToRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Convierte radianes a grados
 */
export function radToDeg(radians: number): number {
  return radians * (180 / Math.PI);
}